import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getStoredValue, setStoredValue } from '../../utils/StorageUtils';
import { getUserExchanges } from '../../services/UserExchangesService';
import { getAiProviders } from '../../services/AiProvidersService';
import { getStrategies } from '../../services/StrategiesService';
import { getCurrentMarket } from '../../utils/MarketStorage';
import { getCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentUserId } from '../../services/TokenStorage';

const STORAGE_KEY = 'onboardingDismissed';

// Passos que o servidor consegue confirmar sozinho (sem depender do usuário marcar). Espelham os
// passos do Assistente de configuração (SetupAssistant) — mesma fonte de verdade.
const AUTO_DETECTABLE_KEYS = ['exchange', 'ai', 'strategy'];

const STEPS = [
    {
        key: 'exchange',
        label: 'Configure sua Corretora',
        description: 'Conecte sua conta da corretora (Binance, Bybit, OKX ou Bitget) informando a chave de API com permissão de leitura e negociação.',
        link: '/settings',
        linkText: 'Ir para Configurações → Corretora'
    },
    {
        key: 'ai',
        label: 'Configure a IA (opcional)',
        description: 'Conecte um provedor de IA (Claude, GPT, Groq...) para obter sinais inteligentes de compra/venda baseados em análise de mercado.',
        link: '/settings',
        linkText: 'Ir para Configurações → IA'
    },
    {
        key: 'monitor',
        label: 'Crie um Monitor',
        description: 'Monitores coletam dados de mercado. Use "Criar configuração inicial" na página de Monitores para criar um monitor EMA pré-configurado.',
        link: '/monitors',
        linkText: 'Ir para Monitores'
    },
    {
        key: 'strategy',
        label: 'Use um Template de Estratégia',
        description: 'Escolha um template pronto (AI Trader, EMA Crossover, DCA com Regime) e crie todas as automações necessárias em um clique.',
        link: '/strategies',
        linkText: 'Ver Templates'
    },
    {
        key: 'activate',
        label: 'Ative sua Estratégia',
        description: 'Após revisar os componentes criados, ative a estratégia para que o sistema comece a operar automaticamente.',
        link: '/strategies',
        linkText: 'Minhas Estratégias'
    }
];

function OnboardingChecklist({ onOpenAssistant }) {
    // Escopado por usuário — sem isso, dispensar/marcar num login vaza pra qualquer OUTRA conta
    // logada no mesmo navegador (mesmo bug corrigido no SetupAssistant).
    const userId = getCurrentUserId();
    const scopedStorageKey = `${STORAGE_KEY}:${userId}`;
    const scopedCheckedKey = `onboardingChecked:${userId}`;
    // getStoredValue/setStoredValue só fazem round-trip correto de STRINGS — localStorage
    // serializa boolean/objeto com toString() (`false`→"false", `{}`→"[object Object]"), e essas
    // strings voltam TRUTHY na próxima leitura. Por isso guardamos como JSON aqui.
    const [dismissed, setDismissed] = useState(() => getStoredValue(scopedStorageKey, 'false') === 'true');
    const [checked, setChecked] = useState(() => {
        try { return JSON.parse(getStoredValue(scopedCheckedKey, '{}')); } catch { return {}; }
    });
    // Passos detectados de verdade no servidor (corretora/IA/estratégia) — sobrepõem o checkbox manual.
    const [autoDetected, setAutoDetected] = useState({});

    useEffect(() => {
        if (dismissed) return;
        const market = getCurrentMarket();
        const environment = getCurrentEnvironment();
        Promise.all([
            getUserExchanges().catch(() => []),
            getAiProviders().catch(() => []),
            getStrategies('', 1, 100, environment, market).catch(() => ({ rows: [] })),
        ]).then(([exchanges, aiProviders, stratRes]) => {
            setAutoDetected({
                exchange: (exchanges || []).length > 0,
                ai: (aiProviders || []).length > 0,
                strategy: (((stratRes && stratRes.rows) || []).length > 0)
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissed]);

    if (dismissed) return null;

    function toggleStep(key) {
        if (AUTO_DETECTABLE_KEYS.includes(key)) return; // detectado no servidor — não editável à mão
        const next = { ...checked, [key]: !checked[key] };
        setChecked(next);
        setStoredValue(scopedCheckedKey, JSON.stringify(next));
    }

    function onDismiss() {
        setStoredValue(scopedStorageKey, 'true');
        setDismissed(true);
    }

    function isDone(key) {
        return AUTO_DETECTABLE_KEYS.includes(key) ? !!autoDetected[key] : !!checked[key];
    }

    const completedCount = STEPS.filter(s => isDone(s.key)).length;
    const allDone = completedCount === STEPS.length;

    return (
        <div className='card border-0 shadow mb-4'>
            <div className='card-header d-flex justify-content-between align-items-center py-2'>
                <div>
                    <strong>Primeiros Passos</strong>
                    <span className='badge bg-primary ms-2'>{completedCount}/{STEPS.length}</span>
                </div>
                <div className='d-flex align-items-center gap-2'>
                    {onOpenAssistant && (
                        <button type='button' className='btn btn-sm btn-outline-primary' onClick={onOpenAssistant}>
                            🧭 Assistente de configuração
                        </button>
                    )}
                    <button type='button' className='btn-close btn-sm' onClick={onDismiss} title='Dispensar este guia' />
                </div>
            </div>
            <div className='card-body py-2'>
                {allDone && (
                    <div className='alert alert-success py-2 mb-2 small'>
                        Parabéns! Você configurou tudo. O bot está pronto para operar.
                    </div>
                )}
                <ol className='list-group list-group-flush list-group-numbered'>
                    {STEPS.map(step => {
                        const done = isDone(step.key);
                        const auto = AUTO_DETECTABLE_KEYS.includes(step.key);
                        return (
                            <li key={step.key} className={`list-group-item d-flex align-items-start gap-3 border-0 px-0 py-2 ${done ? 'opacity-75' : ''}`}>
                                <input
                                    type='checkbox'
                                    className='form-check-input mt-1 flex-shrink-0'
                                    checked={done}
                                    disabled={auto}
                                    title={auto ? 'Detectado automaticamente pelo servidor' : ''}
                                    onChange={() => toggleStep(step.key)}
                                />
                                <div>
                                    <div className={`fw-semibold small ${done ? 'text-decoration-line-through text-muted' : ''}`}>
                                        {step.label}
                                        {auto && done && <span className='badge bg-success ms-2' style={{ fontSize: 10 }}>detectado</span>}
                                    </div>
                                    <div className='text-muted small'>{step.description}</div>
                                    {!done && (
                                        <Link to={step.link} className='btn btn-xs btn-outline-primary mt-1 py-0 px-2 small'>
                                            {step.linkText} →
                                        </Link>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}

export default OnboardingChecklist;
